import type { CMSCard, CMSContentItem } from '@/lib/cms/types';
import { ExpandableItem } from './ExpandableColumnStackItem';

/**
 * Mobile slot for a given item index at a given active index.
 * - `active`: left: 0 (full-bleed, 90% width)
 * - `next`:   left: 90% (peeking from the right)
 * - `prev`:   left: -90% (off-screen to the left, shared by all non-active
 *             / non-next items when N >= 4)
 */
type MobileSlot = 'active' | 'next' | 'prev';

/**
 * Extract ExpandableItem data from CMS content item
 * Handles both Card and ImageWithFocalPoint content types
 */
export function extractItemData(item: CMSCard | CMSContentItem, _index: number): ExpandableItem {
  const typename = (item as { __typename?: string }).__typename;
  const itemId = item.sys.id;

  // Handle ImageWithFocalPoint (legacy format from Contentful)
  if (typename === 'ImageWithFocalPoint') {
    const media = item as {
      sys: { id: string };
      image?: { url?: string; alt?: string };
      imageMobile?: { url?: string };
      video?: { url?: string };
      videoMobile?: { url?: string };
      alt?: string;
      internalName?: string;
    };

    // Use video from Contentful only; render no video when absent
    const videoUrl = media.video?.url || null;

    return {
      id: itemId,
      // Use alt text or internalName as display title for ImageWithFocalPoint
      title: media.alt || media.internalName || null,
      subtitle: null,
      imageUrl: media.image?.url || null,
      imageMobileUrl: media.imageMobile?.url || null,
      videoUrl,
      videoMobileUrl: media.videoMobile?.url || null,
      alt: media.alt || media.internalName || null,
    };
  }

  // Handle Card (preferred format with full content support)
  const card = item as CMSCard;

  // Use video from Contentful only; render no video when absent
  const videoUrl = card.video?.url || null;

  return {
    id: itemId,
    title: card.heading || null,
    subtitle: card.subHeading || null,
    imageUrl: card.image?.url || null,
    imageMobileUrl: card.mobileImage?.url || null,
    videoUrl,
    videoMobileUrl: card.mobileVideo?.url || null,
    alt: card.image?.alt || card.heading || null,
  };
}

export function getMobileSlot(
  itemIndex: number,
  activeIndex: number,
  itemCount: number
): MobileSlot {
  if (itemIndex === activeIndex) return 'active';
  if (itemIndex === (activeIndex + 1) % itemCount) return 'next';
  return 'prev';
}

/**
 * Return the set of item indices whose CSS `left` transition must be
 * suppressed for one frame during the transition `oldActive -> newActive`
 * so that items moving between `prev` and `next` (non-adjacent slots)
 * teleport instead of sliding across the viewport.
 */
export function computeTeleportingIndices(
  oldActive: number,
  newActive: number,
  itemCount: number
): Set<number> {
  const result = new Set<number>();
  for (let i = 0; i < itemCount; i++) {
    const oldSlot = getMobileSlot(i, oldActive, itemCount);
    const newSlot = getMobileSlot(i, newActive, itemCount);
    const isCrossScreenJump =
      (oldSlot === 'prev' && newSlot === 'next') || (oldSlot === 'next' && newSlot === 'prev');
    if (isCrossScreenJump) {
      result.add(i);
    }
  }
  return result;
}

/**
 * Resolve the aria-label for an item's control button.
 * Active items get play/pause; inactive items get a navigation label.
 */
export function getControlAriaLabel(
  isActive: boolean,
  isPlaying: boolean,
  title: string | null | undefined,
  index: number
): string {
  if (!isActive) {
    const slideLabel = title ?? `slide ${index + 1}`;
    return `Go to ${slideLabel}`;
  }
  return isPlaying ? 'Pause video' : 'Play video';
}

/**
 * Resolve the icon for an item's control button.
 * Active items show play/pause; inactive items show the expand icon.
 */
export function getControlIcon(isActive: boolean, isPlaying: boolean): JSX.Element {
  if (!isActive) return <ExpandIcon />;
  return isPlaying ? <PauseIcon /> : <PlayIcon />;
}

/**
 * Pause Icon SVG
 */
function PauseIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="6" y="4" width="4" height="16" fill="currentColor" />
      <rect x="14" y="4" width="4" height="16" fill="currentColor" />
    </svg>
  );
}

/**
 * Play Icon SVG
 */
function PlayIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M8 5V19L19 12L8 5Z" fill="currentColor" />
    </svg>
  );
}

/**
 * Expand/Cross Icon SVG
 */
function ExpandIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <line x1="12" y1="4" x2="12" y2="20" stroke="currentColor" strokeWidth="2" />
      <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}
